import { type Locales } from '@/i18n/i18n-types'
import { TenantId } from './tenant-ids'

// ──────────────────────────────────────────────────────────────────────────────
// IMPORTANT: this module must stay node-importable (no asset/value imports that
// require Vite). `scripts/set-tenant.ts` imports the `tenants` map under tsx to
// generate each tenant's `logos.ts` from the logo filenames declared below.
// The resolved logo assets + active `tenant` live in `tenant.ts`.
// ──────────────────────────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────────────
// Color palette — hex values applied as CSS variables by ThemeProvider
// ──────────────────────────────────────────────────────────────────────────────
export interface TenantColors {
	background: string
	foreground: string
	card: string
	cardForeground: string
	popover: string
	popoverForeground: string
	primary: string
	primaryForeground: string
	secondary: string
	secondaryForeground: string
	muted: string
	mutedForeground: string
	accent: string
	accentForeground: string
	destructive: string
	destructiveForeground: string
	border: string
	input: string
	ring: string
}

export interface TenantFonts {
	/** CSS font-family value applied to body via --font-sans */
	sans: string
}

// ──────────────────────────────────────────────────────────────────────────────
// Feature flags
// ──────────────────────────────────────────────────────────────────────────────
/**
 * How a customer authenticates in the account-based onboarding flow.
 * 'anonymous' = auto customer token, no login UI and login is never "required".
 * `required` only exists on account-based methods. 'email' is not built yet.
 */
export type LoginConfig =
	| { method: 'anonymous' }
	| { method: 'taktikal'; required: boolean }
	| { method: 'email'; required: boolean }

/**
 * Onboarding experience selection.
 * 'fullscreen' = account-based full-screen welcome/login/intro flow with T&C + loyalty pages.
 * 'carousel' = anonymous ecommerce; informational small modal carousel, no login/terms.
 */
export type OnboardingConfig =
	| {
			style: 'fullscreen'
			login: LoginConfig
			/** ISO 8601 date string — users must re-accept terms after this date */
			termsLastUpdatedAt: string
	  }
	| { style: 'carousel' }

export interface TenantFeatures {
	/** Show the product catalog, cart, and checkout flows */
	ecomEnabled: boolean
	/** Default locale for the tenant */
	defaultLocale: Locales
	allowedLocales: Locales[]
	/** Show loyalty card tab + balance */
	loyaltyCardEnabled: boolean
	/** ISO 4217 currency code used for display formatting */
	currency: string
	/** Display name shown in onboarding + page titles */
	name: string
	/** Onboarding style + login/terms configuration */
	onboarding: OnboardingConfig
}

// ──────────────────────────────────────────────────────────────────────────────
// Per-component className overrides (consumed via useOverrides hook)
// ──────────────────────────────────────────────────────────────────────────────
export interface TenantOverrides {
	Button?: {
		default?: string
		outline?: string
	}
	ProductCard?: {
		root?: string
		title?: string
		price?: string
	}
	AddToCartButton?: {
		root?: string
	}
}

// ──────────────────────────────────────────────────────────────────────────────
// Full tenant definition
// ──────────────────────────────────────────────────────────────────────────────
export interface TenantConfig {
	/** Publishable API key (mirrors VITE_PUBLISHABLE_KEY) */
	publishableKey: string
	/** Backend base URL; __BACKEND_URL__ at build-time takes precedence */
	apiUrl: string
	/**
	 * Large header/welcome logo filename, relative to the tenant folder
	 * (`tenants/<tenant>/`). `.svg` files are bundled as theme-tinted inline SVG
	 * components; raster files (`.png/.webp/.jpg`) as image URLs. `set-tenant.ts`
	 * turns this into the tenant's generated `logos.ts`. Undefined → consumers
	 * fall back to the tenant name text.
	 */
	logoLargeFile?: string
	/**
	 * Small logo filename, relative to the tenant folder. Same resolution rules as
	 * `logoLargeFile`. Undefined → consumers render nothing in its place.
	 */
	logoSmallFile?: string
	colors: TenantColors
	fonts: TenantFonts
	features: TenantFeatures
	overrides: TenantOverrides
}

// ──────────────────────────────────────────────────────────────────────────────
// Shared defaults
// ──────────────────────────────────────────────────────────────────────────────
const defaultColors: TenantColors = {
	background: '#ffffff',
	foreground: '#0a0a0a',
	card: '#ffffff',
	cardForeground: '#0a0a0a',
	popover: '#ffffff',
	popoverForeground: '#0a0a0a',
	primary: '#065f46',
	primaryForeground: '#fafafa',
	secondary: '#f5f5f5',
	secondaryForeground: '#171717',
	muted: '#f5f5f5',
	mutedForeground: '#737373',
	accent: '#f5f5f5',
	accentForeground: '#171717',
	destructive: '#ef4444',
	destructiveForeground: '#fafafa',
	border: '#e5e5e5',
	input: '#e5e5e5',
	ring: '#065f46'
}

// ──────────────────────────────────────────────────────────────────────────────
// Tenant map — single source of truth for all tenants
// ──────────────────────────────────────────────────────────────────────────────
export const tenants: Record<TenantId, TenantConfig> = {
	demo: {
		publishableKey: 'pk_e93d6afde970dd1336187ac42db2a751b403393057074e0c2b97a43bb7ab8376',
		apiUrl: 'https://api.demo.fast-track.io',
		logoLargeFile: 'logo-large.png',
		colors: {
			...defaultColors,
			primary: '#013A42',
			ring: '#CFFFE5'
		},
		fonts: { sans: 'Inter, sans-serif' },
		features: {
			defaultLocale: 'is',
			allowedLocales: ['is', 'en', 'nb'],
			ecomEnabled: true,
			loyaltyCardEnabled: false,
			currency: 'ISK',
			name: 'Fast Track Demo',
			onboarding: { style: 'carousel' }
		},
		overrides: {}
	},

	['netto-app']: {
		publishableKey: 'pk_ef80033792d9c2f5d5b6cfd77153bef188222d328d4fa05d1ec0294db363f4ca',
		apiUrl: 'https://api.samkaup.fast-track.io',
		logoLargeFile: 'logo-large.svg',
		logoSmallFile: 'logo-small.svg',
		colors: {
			...defaultColors,
			primary: '#1B4825',
			primaryForeground: '#FBF7ED',
			secondary: '#FBF7ED',
			secondaryForeground: '#1B4825',
			accent: '#FFD936',
			accentForeground: '#1B4825',
			destructive: '#ef4444',
			destructiveForeground: '#ffffff',
			background: '#F3F3F3',
			foreground: '#303D3D',
			card: '#ffffff',
			cardForeground: '#000000',
			popover: '#ffffff',
			popoverForeground: '#374151',
			muted: '#f5f5f5',
			mutedForeground: '#6b7280',
			border: '#e0e0e0',
			input: '#e0e0e0',
			ring: '#1B4825'
		},
		fonts: { sans: 'Inter, sans-serif' },
		features: {
			defaultLocale: 'is',
			allowedLocales: ['is', 'en'],
			ecomEnabled: false,
			loyaltyCardEnabled: true,
			currency: 'ISK',
			name: 'Nettó',
			onboarding: {
				style: 'fullscreen',
				login: { method: 'taktikal', required: true },
				termsLastUpdatedAt: '2025-01-01'
			}
		},
		overrides: {}
	},

	ok: {
		publishableKey: 'pk_2afa5b9354d52be28a96f817814fab821f5be9a79df88a19fea781da5cbeb12d',
		apiUrl: 'https://api.demo.fast-track.io',
		logoLargeFile: 'logo-large.png',
		colors: {
			...defaultColors,
			background: '#eef1f1',
			foreground: '#333333',
			card: '#ffffff',
			cardForeground: '#333333',
			popover: '#ffffff',
			popoverForeground: '#333333',
			primary: '#ffab00',
			primaryForeground: '#000000',
			secondary: '#f7f7f7',
			secondaryForeground: '#000000',
			muted: '#f7f7f7',
			mutedForeground: '#4d4d4d',
			accent: '#f7f7f7',
			accentForeground: '#000000',
			ring: '#333333'
		},
		fonts: { sans: 'Inter, sans-serif' },
		features: {
			defaultLocale: 'is',
			allowedLocales: ['is', 'en'],
			ecomEnabled: true,
			loyaltyCardEnabled: false,
			currency: 'ISK',
			name: 'OK',
			onboarding: { style: 'carousel' }
		},
		overrides: {}
	},

	pris: {
		publishableKey: 'pk_25cb934db15b2293eac37c1cef2f00417147d46141384b9b792c8674014430c6',
		apiUrl: 'https://api.pris.fast-track.io',
		logoLargeFile: 'logo-large.png',
		colors: {
			...defaultColors,
			primary: '#87d600',
			primaryForeground: '#0a0a0a',
			ring: '#87d600'
		},
		fonts: { sans: 'Inter, sans-serif' },
		features: {
			defaultLocale: 'is',
			allowedLocales: ['is', 'en'],
			ecomEnabled: true,
			loyaltyCardEnabled: false,
			currency: 'ISK',
			name: 'Prís',
			onboarding: { style: 'carousel' }
		},
		overrides: {}
	},

	skal: {
		publishableKey: 'pk_e63876424128f6d65245882406ee896722f6692864020443cb22f587af02e763',
		apiUrl: 'https://api.skal.fast-track.io',
		logoLargeFile: 'logo-large.png',
		colors: {
			...defaultColors,
			primary: '#f5003d',
			primaryForeground: '#fafafa',
			ring: '#f5003d'
		},
		fonts: { sans: 'Inter, sans-serif' },
		features: {
			defaultLocale: 'is',
			allowedLocales: ['is', 'en'],
			ecomEnabled: true,
			loyaltyCardEnabled: false,
			currency: 'ISK',
			name: 'Skál',
			onboarding: { style: 'carousel' }
		},
		overrides: {}
	},

	strongpoint: {
		publishableKey: 'pk_73a944de6a7adf4c46f308d7b40a2898daadfc1cc291827392a9075bc3f5e56f',
		apiUrl: 'https://api.strongpoint.fast-track.io',
		logoLargeFile: 'logo-large.png',
		colors: {
			...defaultColors,
			primary: '#3d47d8',
			primaryForeground: '#fafafa',
			ring: '#3d47d8'
		},
		fonts: { sans: 'Inter, sans-serif' },
		features: {
			defaultLocale: 'nb' as Locales,
			allowedLocales: ['en', 'nb'],
			ecomEnabled: true,
			loyaltyCardEnabled: false,
			currency: 'NOK',
			name: 'Strongpoint',
			onboarding: { style: 'carousel' }
		},
		overrides: {}
	},

	nfkino: {
		publishableKey: 'pk_6923dcb22c48714b415d137e2577e242fe451d9387ced0074250ceb16b7918e1',
		apiUrl: 'https://api.strongpoint.fast-track.io',
		logoLargeFile: 'logo-large.png',
		colors: {
			...defaultColors,
			primary: '#040404',
			primaryForeground: '#ffffff',
			ring: '#990000'
		},
		fonts: { sans: 'Inter, sans-serif' },
		features: {
			defaultLocale: 'nb' as Locales,
			allowedLocales: ['en', 'nb'],
			ecomEnabled: true,
			loyaltyCardEnabled: false,
			currency: 'NOK',
			name: 'NFkino',
			onboarding: { style: 'carousel' }
		},
		overrides: {}
	},

	tasty: {
		publishableKey: 'pk_8c19514b5b319f75c647cff0d25d8654212a9468592423895c4a28e494c74157',
		apiUrl: 'https://api.tasty.fast-track.io',
		logoLargeFile: 'logo-large.png',
		colors: {
			...defaultColors,
			primary: '#cf171a',
			primaryForeground: '#fafafa',
			ring: '#cf171a'
		},
		fonts: { sans: 'Inter, sans-serif' },
		features: {
			defaultLocale: 'nb',
			allowedLocales: ['en', 'nb'],
			ecomEnabled: true,
			loyaltyCardEnabled: false,
			currency: 'NOK',
			name: 'Tasty',
			onboarding: { style: 'carousel' }
		},
		overrides: {}
	}
}
